// Vender Review - Admin

import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { doc, getDoc, updateDoc, serverTimestamp, arrayUnion } from 'firebase/firestore';
import { getStorage, ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { auth, db } from '../firebase';
import { onAuthStateChanged } from 'firebase/auth';
import { VendorSpecificModal, VendorSpecificDisplay } from '../components/VendorSpecificModals';
import { MapContainer, TileLayer, Marker, Circle } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import icon from 'leaflet/dist/images/marker-icon.png';
import iconShadow from 'leaflet/dist/images/marker-shadow.png';
import { logChanges } from '../services/auditService';
import { ALL_VENDOR_TYPES } from '../constants/vendorOptions';
import { US_STATES } from '../constants/vendorOptions';
import { formatPhoneDisplay, formatPhoneInput } from '../utils/formatters';
import { computeServiceAreaStatusFields } from '../utils/serviceArea';
import { SHOW_INSURANCE_FEATURE } from '../constants/featureFlags';
import ImageCropModal, { TARGET_ASPECT_RATIO } from '../components/ImageCropModal';
import CroppedImage from '../components/CroppedImage';
import ConfirmModal from '../components/ConfirmModal';
import PromptModal from '../components/PromptModal';
import CityAutocomplete from '../components/CityAutocomplete';

// A gallery image entry can be either a plain URL string (older vendor
// records, before per-image crop data existed) or an object carrying the
// URL plus crop position ({ url, cropX, cropY, cropWidth, cropHeight }).
// This extracts just the URL regardless of which shape is present.
function getImageUrl(entry) {
  return typeof entry === 'string' ? entry : entry?.url;
}
const scrollModalToTop = () => {
  document.querySelector('[data-modal-overlay]')?.scrollTo({ top: 0, behavior: 'smooth' });
};

const DefaultIcon = L.icon({
  iconUrl: icon,
  shadowUrl: iconShadow,
  iconSize: [25, 41],
  iconAnchor: [12, 41],
});
L.Marker.prototype.options.icon = DefaultIcon;

function getZoomForRadius(miles) {
  if (miles <= 10) return 9.5;
  if (miles <= 25) return 8.5;
  if (miles <= 50) return 8;
  if (miles <= 75) return 7.5;
  if (miles <= 100) return 7;
  return 6.5;
}

const STATUS_COLORS = {
  approved: '#4CAF50',
  rejected: '#E53935',
  pending_review: '#2196F3',
  pending: '#FF9800',
};

const STATUS_LABELS = {
  approved: 'Approved',
  rejected: 'Rejected',
  pending_review: 'Update Pending',
  pending: 'Pending Approval',
};

export default function AdminVendorReview() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [vendor, setVendor] = useState(null);
  const [messageCount, setMessageCount] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [mapCoords, setMapCoords] = useState(null);
  const [editingAccountContact, setEditingAccountContact] = useState(false);
  const [editingBusinessInfo, setEditingBusinessInfo] = useState(false);
  const [editingSocialMedia, setEditingSocialMedia] = useState(false);
  const [editingServiceArea, setEditingServiceArea] = useState(false);
  const [editingVendorSpecific, setEditingVendorSpecific] = useState(false);
  const [mediaLoading, setMediaLoading] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [showAuditLog, setShowAuditLog] = useState(false);
  const [showReportLog, setShowReportLog] = useState(false);
  const [reportLogEntries, setReportLogEntries] = useState([]);
  const [reportLogLoading, setReportLogLoading] = useState(false);
  const [auditEntries, setAuditEntries] = useState([]);
  const [showMediaRightsLog, setShowMediaRightsLog] = useState(false);
  const [mediaRightsEntries, setMediaRightsEntries] = useState([]);
  const [mediaRightsLoading, setMediaRightsLoading] = useState(false);

  const formatAuditLogAsText = () => {
    const vendorName = vendor?.companyName || 'Vendor';
    const lines = [`Audit Log — ${vendorName}`, ''];
    auditEntries.forEach((entry) => {
      const who = entry.changedBy === 'admin'
        ? (entry.changedByName ? `Admin - ${entry.changedByName}` : 'Admin')
        : 'Vendor';
      const ts = entry.changedAt?.toDate ? entry.changedAt.toDate() : null;
      const timeStr = ts ? ts.toLocaleString() : '—';
      lines.push(`${who} — ${timeStr}`);
      (entry.changes || []).forEach((change) => {
        lines.push(`  ${change.field}: "${change.from}" → "${change.to}"`);
      });
      lines.push('');
    });
    return lines.join('\n');
  };

  const handleDownloadAuditLog = () => {
    const text = formatAuditLogAsText();
    const blob = new Blob([text], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `audit-log-${vendor?.companyName || 'vendor'}.txt`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const handlePrintAuditLog = () => {
    const vendorName = vendor?.companyName || 'Vendor';
    const printWindow = window.open('', '_blank');
    const rows = auditEntries.map((entry) => {
      const who = entry.changedBy === 'admin'
        ? (entry.changedByName ? `Admin - ${entry.changedByName}` : 'Admin')
        : 'Vendor';
      const ts = entry.changedAt?.toDate ? entry.changedAt.toDate() : null;
      const timeStr = ts ? ts.toLocaleString() : '—';
      const changeLines = (entry.changes || [])
        .map((c) => `<div style="margin-left:16px;">${c.field}: "${c.from}" &rarr; "${c.to}"</div>`)
        .join('');
      return `<div style="margin-bottom:16px;"><strong>${who}</strong> — ${timeStr}${changeLines}</div>`;
    }).join('');
    printWindow.document.write(`
      <html>
        <head><title>Audit Log — ${vendorName}</title></head>
        <body style="font-family: sans-serif; padding: 24px;">
          <h2>Audit Log — ${vendorName}</h2>
          ${rows || '<p>No changes recorded yet.</p>'}
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.print();
  };

  const formatMediaRightsLogAsText = () => {
    const vendorName = vendor?.companyName || 'Vendor';
    const lines = [`Media Rights Confirmations — ${vendorName}`, ''];
    mediaRightsEntries.forEach((entry) => {
      const ts = entry.timestamp?.toDate ? entry.timestamp.toDate() : null;
      const timeStr = ts ? ts.toLocaleString() : '—';
      lines.push(`User: ${entry.userId} — ${timeStr}`);
      lines.push(`  Media: ${entry.mediaId}`);
      lines.push(`  Terms Version: ${entry.termsVersion}`);
      lines.push(`  Confirmed: ${entry.confirmationTimestamp}`);
      lines.push(`  IP: ${entry.ipAddress || '—'}`);
      lines.push(`  Device: ${entry.deviceInfo || '—'}`);
      lines.push('');
    });
    return lines.join('\n');
  };

  const handleDownloadMediaRightsLog = () => {
    const text = formatMediaRightsLogAsText();
    const blob = new Blob([text], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `media-rights-log-${vendor?.companyName || 'vendor'}.txt`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const handlePrintMediaRightsLog = () => {
    const vendorName = vendor?.companyName || 'Vendor';
    const printWindow = window.open('', '_blank');
    const rows = mediaRightsEntries.map((entry) => {
      const ts = entry.timestamp?.toDate ? entry.timestamp.toDate() : null;
      const timeStr = ts ? ts.toLocaleString() : '—';
      return `<div style="margin-bottom:16px;"><strong>User:</strong> ${entry.userId} — ${timeStr}
        <div style="margin-left:16px;">Media: ${entry.mediaId}</div>
        <div style="margin-left:16px;">Terms Version: ${entry.termsVersion}</div>
        <div style="margin-left:16px;">Confirmed: ${entry.confirmationTimestamp}</div>
        <div style="margin-left:16px;">IP: ${entry.ipAddress || '—'}</div>
        <div style="margin-left:16px;">Device: ${entry.deviceInfo || '—'}</div>
      </div>`;
    }).join('');
    printWindow.document.write(`
      <html>
        <head><title>Media Rights Confirmations — ${vendorName}</title></head>
        <body style="font-family: sans-serif; padding: 24px;">
          <h2>Media Rights Confirmations — ${vendorName}</h2>
          ${rows || '<p>No confirmations recorded yet.</p>'}
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.print();
  };

  const [auditLoading, setAuditLoading] = useState(false);
  const [selectedMedia, setSelectedMedia] = useState(null);
  const [previewMode, setPreviewMode] = useState('full');

  // Matches the same mechanism as pending media — content actually
  // moves out of reelUrl/imageUrls (the fields every display screen
  // already reads) rather than staying there behind a separate flag.
  // This means no display screen needs its own hidden-content check at
  // all; they simply never see it in the first place.
  const handleToggleReelHidden = async () => {
    if (vendor.hiddenReelUrl) {
      // Currently hidden — restore it.
      await saveField({
        reelUrl: vendor.hiddenReelUrl,
        hasReel: true,
        hiddenReelUrl: null,
        reelHidden: false,
      });
    } else {
      // Currently visible — hide it.
      await saveField({
        hiddenReelUrl: vendor.reelUrl,
        reelUrl: null,
        hasReel: false,
        reelHidden: true,
      });
    }
  };

  const handleToggleImageHidden = async (url) => {
    const liveUrls = vendor.imageUrls || [];
    const hiddenUrls = vendor.hiddenImageUrls || [];
    const isCurrentlyHidden = hiddenUrls.some((e) => e.url === url);

    if (isCurrentlyHidden) {
      // Restore it to its original position, not just appended to the
      // end — clamped to the current length, in case other images were
      // added/removed while this one was hidden.
      const entry = hiddenUrls.find((e) => e.url === url);
      const { originalIndex, ...restEntry } = entry;
      const newLiveUrls = [...liveUrls];
      const insertAt = Math.min(originalIndex ?? newLiveUrls.length, newLiveUrls.length);
      newLiveUrls.splice(insertAt, 0, restEntry);
      await saveField({
        imageUrls: newLiveUrls,
        imageCount: newLiveUrls.length,
        hiddenImageUrls: hiddenUrls.filter((e) => e.url !== url),
      });
    } else {
      // Hide it — remember exactly where it was, so it can go back to
      // that same spot later.
      const originalIndex = liveUrls.findIndex((e) => e.url === url);
      const entry = liveUrls[originalIndex];
      const newLiveUrls = liveUrls.filter((e) => e.url !== url);
      await saveField({
        imageUrls: newLiveUrls,
        imageCount: newLiveUrls.length,
        hiddenImageUrls: [...hiddenUrls, { ...entry, originalIndex }],
      });
    }
  };

  const handleDeleteReel = async () => {
    setMediaLoading(true);
    try {
      const storage = getStorage();
      const reelRef = ref(storage, `vendors/${id}/reel/reel.mp4`);
      await deleteObject(reelRef);
      await saveField({ reelUrl: null, hasReel: false });
    } catch (err) {
      console.error('Reel delete error:', err);
    }
    setMediaLoading(false);
  };

  const handleDeletePendingImage = async (index) => {
    setMediaLoading(true);
    try {
      const currentPending = [...(vendor.pendingImageUrls || [])];
      const [removed] = currentPending.splice(index, 1);
      if (removed?.url) {
        try {
          const storage = getStorage();
          const imgRef = ref(storage, removed.url);
          await deleteObject(imgRef);
        } catch (storageErr) {
          console.error('Pending image storage delete error (continuing anyway):', storageErr);
        }
      }
      await saveField({ pendingImageUrls: currentPending });
    } catch (err) {
      console.error('Pending image delete error:', err);
    }
    setMediaLoading(false);
  };

  const handleCancelPendingReel = async () => {
    setMediaLoading(true);
    try {
      const storage = getStorage();
      const reelRef = ref(storage, `vendors/${id}/reel/pending_reel.mp4`);
      await deleteObject(reelRef);
      await saveField({ pendingReelUrl: null });
    } catch (err) {
      console.error('Pending reel cancel error:', err);
    }
    setMediaLoading(false);
  };

  const [draggedImageIndex, setDraggedImageIndex] = useState(null);

  const handleImageDrop = async (targetIndex) => {
    if (draggedImageIndex === null || draggedImageIndex === targetIndex) {
      setDraggedImageIndex(null);
      return;
    }
    const reordered = [...(vendor.imageUrls || [])];
    const [moved] = reordered.splice(draggedImageIndex, 1);
    reordered.splice(targetIndex, 0, moved);
    setDraggedImageIndex(null);
    await saveField({ imageUrls: reordered });
  };

  const handleReelSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setMediaLoading(true);
    try {
      const storage = getStorage();
      // Admin cannot confirm rights on the vendor's behalf — this saves
      // to a separate pending field the vendor must review and confirm
      // themselves before it goes live. Uploaded under a distinct path
      // (not vendors/{id}/reel/reel.mp4) so it doesn't silently
      // overwrite whatever reel is already live and confirmed.
      const reelRef = ref(storage, `vendors/${id}/reel/pending_reel.mp4`);
      await uploadBytes(reelRef, file);
      const url = await getDownloadURL(reelRef);
      await saveField({ pendingReelUrl: url });
    } catch (err) {
      console.error('Reel upload error:', err);
    }
    setMediaLoading(false);
  };

  const [cropQueue, setCropQueue] = useState([]);
  const [croppingFile, setCroppingFile] = useState(null);

  const uploadOneImage = async (file, cropData) => {
    const storage = getStorage();
    // Uploaded under a distinct pending path — kept entirely separate
    // from the live gallery until the vendor reviews and confirms it,
    // since admin cannot confirm content rights on the vendor's behalf.
    const currentPending = [...(vendor.pendingImageUrls || [])];
    const index = (vendor.imageUrls || []).length + currentPending.length;
    const imgRef = ref(storage, `vendors/${id}/gallery/pending_image_${index}.jpg`);
    await uploadBytes(imgRef, file);
    const url = await getDownloadURL(imgRef);
    const newPending = [
      ...currentPending,
      {
        url,
        cropX: cropData?.cropX ?? 0,
        cropY: cropData?.cropY ?? 0,
        cropWidth: cropData?.cropWidth ?? null,
        cropHeight: cropData?.cropHeight ?? null,
      },
    ];
    await saveField({ pendingImageUrls: newPending });
  };

  const handleImagesSelect = (e) => {
    const files = Array.from(e.target.files);
    const currentCount = (vendor.imageUrls || []).length;
    const remaining = 9 - currentCount;
    const toQueue = files.slice(0, remaining);
    if (toQueue.length === 0) return;
    setCropQueue(toQueue);
    setCroppingFile({ file: toQueue[0], previewUrl: URL.createObjectURL(toQueue[0]) });
  };

  const advanceCropQueue = () => {
    const remainingQueue = cropQueue.slice(1);
    setCropQueue(remainingQueue);
    if (remainingQueue.length > 0) {
      setCroppingFile({ file: remainingQueue[0], previewUrl: URL.createObjectURL(remainingQueue[0]) });
    } else {
      setCroppingFile(null);
    }
  };

  const handleCropConfirm = async (cropData) => {
    const file = croppingFile.file;
    advanceCropQueue();
    setMediaLoading(true);
    try {
      await uploadOneImage(file, cropData);
    } catch (err) {
      console.error('Image upload error:', err);
    }
    setMediaLoading(false);
  };

  const handleCropCancel = () => {
    advanceCropQueue();
  };

  const [editingCropIndex, setEditingCropIndex] = useState(null);

  const handleEditCropConfirm = async (cropData) => {
    const index = editingCropIndex;
    setEditingCropIndex(null);
    setMediaLoading(true);
    try {
      const urls = [...(vendor.imageUrls || [])];
      const existingUrl = getImageUrl(urls[index]);
      urls[index] = {
        url: existingUrl,
        cropX: cropData.cropX,
        cropY: cropData.cropY,
        cropWidth: cropData.cropWidth,
        cropHeight: cropData.cropHeight,
        panX: cropData.panX,
        panY: cropData.panY,
        zoom: cropData.zoom,
      };
      await saveField({ imageUrls: urls, imageCount: urls.length });
    } catch (err) {
      console.error('Crop update error:', err);
    }
    setMediaLoading(false);
  };

  const [confirmingDeleteIndex, setConfirmingDeleteIndex] = useState(null);

  const deleteImage = (index) => {
    setConfirmingDeleteIndex(index);
  };

  const handleConfirmDeleteImage = async () => {
    const index = confirmingDeleteIndex;
    setConfirmingDeleteIndex(null);
    setMediaLoading(true);
    try {
      const urls = [...(vendor.imageUrls || [])];
      const urlToDelete = getImageUrl(urls[index]);
      urls.splice(index, 1);
      try {
        const storage = getStorage();
        const imgRef = ref(storage, urlToDelete);
        await deleteObject(imgRef);
      } catch (storageErr) {
        // Storage cleanup failing shouldn't block removing it from the
        // vendor's gallery — worst case is an orphaned file in Storage,
        // which is far better than the image staying visible forever.
        console.error('Storage cleanup error (continuing anyway):', storageErr);
      }
      await saveField({ imageUrls: urls, imageCount: urls.length });
    } catch (err) {
      console.error('Image delete error:', err);
    }
    setMediaLoading(false);
  };

  const saveField = async (fields) => {
    const scrollY = window.scrollY;
    const oldData = vendor ? { ...vendor } : {};
    await updateDoc(doc(db, 'vendors', id), fields);
    try {
      await logChanges({
        vendorId: id,
        changedBy: 'admin',
        changedByUid: auth.currentUser?.uid || 'unknown',
        oldData,
        newData: fields,
      });
    } catch (err) {
      console.error('Audit log error:', err);
    }
    const vendorDoc = await getDoc(doc(db, 'vendors', id));
    if (vendorDoc.exists()) {
      setVendor({ id: vendorDoc.id, ...vendorDoc.data() });
    }
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        window.scrollTo(0, scrollY);
      });
    });
  };

  const loadAuditLog = async () => {
    setAuditLoading(true);
    const { collection, query, orderBy, getDocs } = await import('firebase/firestore');
    const q = query(
      collection(db, 'vendors', id, 'auditLog'),
      orderBy('changedAt', 'desc')
    );
    const snap = await getDocs(q);
    setAuditEntries(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    setAuditLoading(false);
  };

  const loadMediaRightsLog = async () => {
    setMediaRightsLoading(true);
    const { collection, query, orderBy, getDocs } = await import('firebase/firestore');
    const q = query(
      collection(db, 'vendors', id, 'mediaRightsConfirmations'),
      orderBy('timestamp', 'desc')
    );
    const snap = await getDocs(q);
    setMediaRightsEntries(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    setMediaRightsLoading(false);
  };

  // Deliberately unfiltered by status — shows every report ever made
  // against this vendor, even ones from before the vendor was
  // cleared/restored, unlike the reports banner which only appears
  // while the vendor is currently flagged or suspended.
  const loadReportLog = async () => {
    setReportLogLoading(true);
    const { collection, query, where, orderBy, getDocs } = await import('firebase/firestore');
    const q = query(
      collection(db, 'reports'),
      where('vendorId', '==', id),
      orderBy('createdAt', 'desc')
    );
    const snap = await getDocs(q);
    setReportLogEntries(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    setReportLogLoading(false);
  };

  const loadVendor = React.useCallback(async () => {
    setLoading(true);
    const vendorDoc = await getDoc(doc(db, 'vendors', id));
    if (vendorDoc.exists()) {
      const data = { id: vendorDoc.id, ...vendorDoc.data() };
      setVendor(data);
      if (data.latitude && data.longitude) {
        setMapCoords({ lat: data.latitude, lng: data.longitude });
      } else if (data.businessAddress?.line1) {
        await geocodeAddress(data.businessAddress);
      }
    }
    setLoading(false);
  }, [id]);

  const loadMessageCount = React.useCallback(async () => {
    try {
      const { collection, query, where, getDocs } = await import('firebase/firestore');
      const q = query(collection(db, 'messages'), where('vendorId', '==', id));
      const snap = await getDocs(q);
      setMessageCount(snap.docs.length);
    } catch (err) {
      console.error('Message count error:', err);
    }
  }, [id]);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (user) => {
      if (!user) {
        navigate('/vendor/login');
        return;
      }
      const adminDoc = await getDoc(doc(db, 'admins', user.uid));
      if (!adminDoc.exists()) {
        navigate('/vendor/login');
        return;
      }
      await loadVendor();
      await loadMessageCount();
    });
    return () => unsub();
  }, [navigate, id, loadVendor, loadMessageCount]);

  const geocodeAddress = async (address) => {
    const fullAddress = `${address.line1}, ${address.city}, ${address.state} ${address.zip}`;
    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(fullAddress)}&format=json&limit=1`
      );
      const data = await response.json();
      if (data && data.length > 0) {
        const coords = { lat: parseFloat(data[0].lat), lng: parseFloat(data[0].lon) };
        setMapCoords(coords);
        return coords;
      }
    } catch (err) {
      console.error('Geocoding error:', err);
    }
    return null;
  };

  const handleBack = () => {
    const params = new URLSearchParams(window.location.search);
    const fromTab = params.get('from') || 'pending';
    const fromSearch = params.get('search');
    navigate(`/admin?tab=${fromTab}${fromSearch ? `&search=${encodeURIComponent(fromSearch)}` : ''}`);
  };

  const [confirmingApprove, setConfirmingApprove] = useState(false);

  const handleApprove = () => {
    setConfirmingApprove(true);
  };

  const handleConfirmApprove = async () => {
    setConfirmingApprove(false);
    setActionLoading(true);
    try {
      const fields = {
        status: 'approved',
        approvedAt: serverTimestamp(),
        statusChangedAt: serverTimestamp(),
      };
      // A vendor created on mobile deliberately saves null coordinates at
      // registration (mobile geocodes on its own approval flow instead).
      // If admin approves from the web side, make sure that gap still
      // gets filled here too, regardless of which platform the vendor
      // originally registered through.
      if ((!vendor.latitude || !vendor.longitude) && vendor.businessAddress) {
        const coords = await geocodeAddress(vendor.businessAddress);
        if (coords) {
          fields.latitude = coords.lat;
          fields.longitude = coords.lng;
        }
      }
      await saveField(fields);
    } catch (err) {
      console.error('Approve error:', err);
    }
    setActionLoading(false);
  };

  const [confirmingReject, setConfirmingReject] = useState(false);

  const handleReject = () => {
    setConfirmingReject(true);
  };

  const handleConfirmReject = async (reason) => {
    setConfirmingReject(false);
    setActionLoading(true);
    try {
      await saveField({
        status: 'rejected',
        rejectionReason: reason.trim(),
        rejectedAt: serverTimestamp(),
        statusChangedAt: serverTimestamp(),
      });
      // Updated separately — logChanges deliberately skips any Firestore
      // sentinel value (like serverTimestamp()), which also catches
      // arrayUnion, silently excluding this from the audit log entirely
      // if it went through saveField above. The history array is
      // already its own permanent record, so it doesn't need audit-log
      // diffing on top of that.
      await updateDoc(doc(db, 'vendors', id), {
        // Firestore doesn't allow serverTimestamp() inside an array
        // element passed to arrayUnion — only as a top-level field — so
        // this uses the admin's own device clock instead.
        rejectionHistory: arrayUnion({
          reason: reason.trim(),
          rejectedAt: new Date(),
        }),
      });
    } catch (err) {
      console.error('Reject error:', err);
    }
    setActionLoading(false);
  };

  const safeString = (value) => {
    if (value == null) return '—';
    if (Array.isArray(value)) return value.length === 0 ? '—' : value.join(', ');
    if (value.toString().length === 0) return '—';
    return value.toString();
  };

  const formatTimestamp = (ts) => {
    if (!ts) return '—';
    try {
      const dt = ts.toDate ? ts.toDate() : new Date(ts.seconds * 1000);
      const period = dt.getHours() >= 12 ? 'PM' : 'AM';
      const hour = dt.getHours() > 12 ? dt.getHours() - 12 : dt.getHours() === 0 ? 12 : dt.getHours();
      return `${dt.getMonth() + 1}/${dt.getDate()}/${dt.getFullYear()} ${hour}:${dt.getMinutes().toString().padStart(2, '0')} ${period}`;
    } catch {
      return '—';
    }
  };

  if (loading || !vendor) {
    return <div style={styles.loading}><p>Loading...</p></div>;
  }

  const status = vendor.status || 'pending';
  const statusColor = STATUS_COLORS[status] || '#999';
  const statusLabel = STATUS_LABELS[status] || status;

  return (
    <div style={styles.container}>
      <nav style={styles.nav}>
        <button onClick={handleBack} style={styles.backButton}>
          ← Back to Dashboard
        </button>
        <span style={styles.navTitle}>{vendor.companyName}</span>
        <button onClick={() => { setShowPreview(true); setSelectedMedia(null); setPreviewMode('full'); }} style={{ padding: '8px 16px', backgroundColor: '#000', color: 'white', border: 'none', borderRadius: '8px', fontSize: '13px', fontWeight: '600', cursor: 'pointer' }}>
          Preview Profile
        </button>
        <button onClick={async () => { setShowAuditLog(true); await loadAuditLog(); }} style={{ padding: '8px 16px', backgroundColor: '#2C3E50', color: 'white', border: 'none', borderRadius: '8px', fontSize: '13px', fontWeight: '600', cursor: 'pointer' }}>
          Audit Log
        </button>
        <button onClick={async () => { setShowReportLog(true); await loadReportLog(); }} style={{ padding: '8px 16px', backgroundColor: '#B91C1C', color: 'white', border: 'none', borderRadius: '8px', fontSize: '13px', fontWeight: '600', cursor: 'pointer' }}>
          Report Log
        </button>
        <button onClick={async () => { setShowMediaRightsLog(true); await loadMediaRightsLog(); }} style={{ padding: '8px 16px', backgroundColor: '#6B4C9A', color: 'white', border: 'none', borderRadius: '8px', fontSize: '13px', fontWeight: '600', cursor: 'pointer' }}>
          Media Rights Log
        </button>
        <div style={styles.navActions}>
          {(status === 'pending' || status === 'pending_review') && (
            <>
              <button
                onClick={handleApprove}
                style={styles.approveButton}
                disabled={actionLoading}
              >
                Approve
              </button>
              <button
                onClick={handleReject}
                style={styles.rejectButton}
                disabled={actionLoading}
              >
                Reject
              </button>
            </>
          )}
          <span style={{
            ...styles.statusBadge,
            backgroundColor: statusColor + '20',
            color: statusColor,
            border: `1px solid ${statusColor}40`,
          }}>
            {statusLabel}
          </span>
        </div>
      </nav>

      <div style={styles.content}>
        {(status === 'flagged' || status === 'suspended') && (
          <FlaggedReportsBanner vendorId={id} />
        )}
        {vendor.serviceAreaMissing && (
          <div style={{ backgroundColor: '#FEE2E2', border: '1px solid rgba(185,28,28,0.3)', borderRadius: '14px', padding: '16px', marginBottom: '16px', display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
            <span style={{ fontSize: '20px', lineHeight: 1 }}>⚠️</span>
            <div>
              <p style={{ margin: 0, color: '#B91C1C', fontWeight: '700', fontSize: '14px' }}>Service Area Data Missing</p>
              <p style={{ margin: '4px 0 0', color: '#B91C1C', fontSize: '13px', lineHeight: 1.4 }}>
                This vendor has no service radius or additional cities set, so they won't appear in location-based search results.
              </p>
            </div>
          </div>
        )}
        {/* Account & Contact */}
        <Section title="Account & Contact" onEdit={() => setEditingAccountContact(true)}>
          <Row label="Company" value={safeString(vendor.companyName)} />
          <Row label="Contact Name" value={safeString(vendor.contactName)} />
          <Row label="Email" value={safeString(vendor.email)} />
          <Row label="Website" value={safeString(vendor.website)} />
          <Row label="Phone" value={formatPhoneDisplay(vendor.phone)} />
          <Row label="Phone Public" value={vendor.phonePublic ? 'Yes' : 'No'} />
          <Row label="Contact Public" value={vendor.contactNamePublic ? 'Yes' : 'No'} />
          <Row label="Vendor Type" value={safeString(vendor.vendorTypes?.[0])} />
          <Row label="Business Address" value={vendor.businessAddress?.line1 ? `${vendor.businessAddress.line1}${vendor.businessAddress.line2 ? `, ${vendor.businessAddress.line2}` : ''}, ${vendor.businessAddress.city}, ${vendor.businessAddress.state} ${vendor.businessAddress.zip}` : '—'} />
          <Row label="Profile Views" value={safeString(vendor.profileViews ?? 0)} />
          <Row label="Unique Viewers" value={safeString((vendor.uniqueViewerIds || []).length)} />
          <Row label="Favorites" value={safeString(vendor.favoriteCount ?? 0)} />
          <Row label="Searches Appeared In" value={safeString(vendor.searchAppearances ?? 0)} />
          <Row label="Website Clicks" value={safeString(vendor.websiteClicks ?? 0)} />
          <Row label="Social Media Clicks (Total)" value={safeString(vendor.socialMediaClicks ?? 0)} />
          <Row label="Instagram Clicks" value={safeString(vendor.instagramClicks ?? 0)} />
          <Row label="Facebook Clicks" value={safeString(vendor.facebookClicks ?? 0)} />
          <Row label="TikTok Clicks" value={safeString(vendor.tiktokClicks ?? 0)} />
          <Row label="Pinterest Clicks" value={safeString(vendor.pinterestClicks ?? 0)} />
          <Row label="Messages Received" value={messageCount !== null ? safeString(messageCount) : 'Loading...'} />
          <Row label="Submitted" value={formatTimestamp(vendor.submittedAt)} />
          {vendor.approvedAt && <Row label="Approved" value={formatTimestamp(vendor.approvedAt)} />}
          {vendor.rejectedAt && <Row label="Rejected" value={formatTimestamp(vendor.rejectedAt)} />}
          {vendor.statusChangedAt && <Row label="Last Status Change" value={formatTimestamp(vendor.statusChangedAt)} />}
          {vendor.rejectionReason && <Row label="Rejection Reason" value={vendor.rejectionReason} />}
          {vendor.resubmittedAt && <Row label="Resubmitted" value={formatTimestamp(vendor.resubmittedAt)} />}
          {(vendor.rejectionHistory || []).map((entry, i) => (
            <Row
              key={i}
              label={`Rejection History (${formatTimestamp(entry.rejectedAt)})`}
              value={entry.reason}
            />
          ))}
        </Section>

        {/* Business Information */}
        <Section title="Business Information" onEdit={() => setEditingBusinessInfo(true)}>
          <Row label="Phone Public" value={vendor.phonePublic ? 'Yes' : 'No'} />
          <Row label="Contact Name Public" value={vendor.contactNamePublic ? 'Yes' : 'No'} />
          <Row label="What Sets You Apart" value={(vendor.differentiators || []).filter((d) => d).join(' • ') || '—'} />
          <Row label="Brief Intro" value={safeString(vendor.briefIntro)} />
          <Row label="Bio" value={safeString(vendor.bio)} />
          <Row label="Languages" value={safeString(vendor.languages)} />
          <Row label="Diversity Tags" value={safeString(vendor.diversityTags)} />
          <Row label="Experience" value={vendor.experienceYear ? `${vendor.experienceYears || 0} years (since ${vendor.experienceYear})` : '—'} />
          {SHOW_INSURANCE_FEATURE && (
            <Row label="Has Insurance" value={vendor.hasInsurance ? 'Yes' : 'No'} />
          )}
        </Section>

        {/* Social Media */}
        <Section title="Social Media" onEdit={() => setEditingSocialMedia(true)}>
          <Row label="Instagram" value={safeString(vendor.instagram)} />
          <Row label="Facebook" value={safeString(vendor.facebook)} />
          <Row label="TikTok" value={safeString(vendor.tiktok)} />
          <Row label="Pinterest" value={safeString(vendor.pinterest)} />
        </Section>

        {/* Service Area */}
        <Section title="Service Area" onEdit={() => setEditingServiceArea(true)}>
          <Row label="Service Radius" value={vendor.serviceRadiusMiles ? `${vendor.serviceRadiusMiles} miles` : '—'} />
          <Row label="Additional Cities" value={safeString(vendor.additionalCities)} />
          {vendor.latitude && <Row label="Coordinates" value={`${vendor.latitude?.toFixed(4)}, ${vendor.longitude?.toFixed(4)}`} />}
          {mapCoords && (
            <div style={{ marginTop: '12px', borderRadius: '12px', overflow: 'hidden', position: 'relative', zIndex: 0 }}>
              <MapContainer
                center={[mapCoords.lat, mapCoords.lng]}
                zoom={vendor.vendorTypes?.[0] === 'Venue' || vendor.vendorTypes?.[0] === 'Wedding Attire' ? 13 : getZoomForRadius(vendor.serviceRadiusMiles || 25)}
                style={{ height: '240px', width: '100%' }}
                key={`${mapCoords.lat}-${mapCoords.lng}`}
              >
                <TileLayer
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                  attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                />
                <Marker position={[mapCoords.lat, mapCoords.lng]} />
                {vendor.serviceRadiusMiles > 0 && vendor.vendorTypes?.[0] !== 'Venue' && vendor.vendorTypes?.[0] !== 'Wedding Attire' && (
                  <Circle
                    center={[mapCoords.lat, mapCoords.lng]}
                    radius={vendor.serviceRadiusMiles * 1609.34}
                    pathOptions={{ color: '#000000', fillColor: '#000000', fillOpacity: 0.1 }}
                  />
                )}
                {(vendor.additionalCitiesCoords || []).map((c, i) => (
                  <Marker key={i} position={[c.latitude, c.longitude]} />
                ))}
              </MapContainer>
            </div>
          )}
        </Section>

        {/* Vendor Specific */}
        <Section title="Vendor Specific Details" onEdit={() => setEditingVendorSpecific(true)}>
          <VendorSpecificDisplay vendor={vendor} safeString={safeString} />
        </Section>

        {/* Media */}
        <Section title="Media">
          {mediaLoading && (
            <p style={{ color: '#888', fontSize: '13px', marginBottom: '8px' }}>Updating media...</p>
          )}
          <Row label="Has Reel" value={vendor.hasReel ? 'Yes' : 'No'} />
          <Row label="Image Count" value={safeString(vendor.imageCount)} />

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '16px', marginBottom: '8px' }}>
            <span style={{ fontSize: '14px', fontWeight: '600', color: '#000000' }}>Intro Reel</span>
            <div style={{ display: 'flex', gap: '8px' }}>
              {!vendor.hiddenReelUrl && (
                <label style={{ ...styles.addMediaButton, ...(mediaLoading ? { opacity: 0.5, pointerEvents: 'none' } : {}) }}>
                  {vendor.reelUrl ? 'Replace' : 'Upload'}
                  <input type="file" accept="video/*" onChange={handleReelSelect} style={{ display: 'none' }} disabled={mediaLoading} />
                </label>
              )}
              {(vendor.reelUrl || vendor.hiddenReelUrl) && (
                <button
                  onClick={handleToggleReelHidden}
                  disabled={mediaLoading}
                  style={{ ...styles.addMediaButton, color: vendor.hiddenReelUrl ? '#166534' : '#B45309', border: `1px solid ${vendor.hiddenReelUrl ? '#166534' : '#B45309'}`, backgroundColor: 'white', cursor: 'pointer', ...(mediaLoading ? { opacity: 0.5, pointerEvents: 'none' } : {}) }}
                >
                  {vendor.hiddenReelUrl ? 'Unhide' : 'Hide'}
                </button>
              )}
              {vendor.reelUrl && (
                <button
                  onClick={handleDeleteReel}
                  disabled={mediaLoading}
                  style={{ ...styles.addMediaButton, color: '#B91C1C', border: '1px solid #B91C1C', backgroundColor: 'white', cursor: 'pointer', ...(mediaLoading ? { opacity: 0.5, pointerEvents: 'none' } : {}) }}
                >
                  Delete
                </button>
              )}
              {vendor.pendingReelUrl && (
                <button
                  onClick={handleCancelPendingReel}
                  disabled={mediaLoading}
                  style={{ ...styles.addMediaButton, color: '#B91C1C', border: '1px solid #B91C1C', backgroundColor: 'white', cursor: 'pointer', ...(mediaLoading ? { opacity: 0.5, pointerEvents: 'none' } : {}) }}
                >
                  Cancel Pending
                </button>
              )}
            </div>
          </div>
          {vendor.reelUrl && (
            <video src={vendor.reelUrl} controls style={{ width: '100%', maxHeight: '300px', borderRadius: '12px', backgroundColor: '#000' }} />
          )}
          {vendor.hiddenReelUrl && (
            <div style={{ position: 'relative' }}>
              <video src={vendor.hiddenReelUrl} controls style={{ width: '100%', maxHeight: '300px', borderRadius: '12px', backgroundColor: '#000' }} />
              <div style={{
                position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
                backgroundColor: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'center',
                justifyContent: 'center', borderRadius: '12px', pointerEvents: 'none',
              }}>
                <span style={{ color: '#fff', fontSize: '13px', fontWeight: '700' }}>Hidden from Users</span>
              </div>
            </div>
          )}
          {vendor.pendingReelUrl && (
            <div style={{ marginTop: '12px', position: 'relative' }}>
              <video src={vendor.pendingReelUrl} controls style={{ width: '100%', maxHeight: '300px', borderRadius: '12px', backgroundColor: '#000' }} />
              <div style={{
                position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
                backgroundColor: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'center',
                justifyContent: 'center', borderRadius: '12px', pointerEvents: 'none',
              }}>
                <span style={{ color: '#fff', fontSize: '13px', fontWeight: '700' }}>Needs User Approval</span>
              </div>
            </div>
          )}

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '20px', marginBottom: '8px' }}>
            <span style={{ fontSize: '14px', fontWeight: '600', color: '#000000' }}>Gallery ({(vendor.imageUrls || []).length}/9)</span>
            {(vendor.imageUrls || []).length < 9 && (
              <label style={{ ...styles.addMediaButton, ...(mediaLoading ? { opacity: 0.5, pointerEvents: 'none' } : {}) }}>
                + Add
                <input type="file" accept="image/*" multiple onChange={handleImagesSelect} style={{ display: 'none' }} disabled={mediaLoading} />
              </label>
            )}
          </div>
          {(vendor.imageUrls || []).length > 1 && (
            <p style={{ fontSize: '12px', color: '#888', marginTop: 0, marginBottom: '8px' }}>Drag and drop to reorder images.</p>
          )}
          {((vendor.imageUrls || []).length > 0 || (vendor.pendingImageUrls || []).length > 0 || (vendor.hiddenImageUrls || []).length > 0) && (
            <div style={styles.imageGrid}>
              {(vendor.imageUrls || []).map((entry, i) => (
                <div
                  key={entry.url}
                  style={{
                    position: 'relative',
                    cursor: mediaLoading ? 'default' : 'grab',
                    opacity: draggedImageIndex === i ? 0.4 : 1,
                  }}
                  draggable={!mediaLoading}
                  onDragStart={() => setDraggedImageIndex(i)}
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={() => handleImageDrop(i)}
                  onDragEnd={() => setDraggedImageIndex(null)}
                >
                  <CroppedImage entry={entry} alt={`Gallery ${i + 1}`} style={styles.image} />
                  <button
                    onClick={() => handleToggleImageHidden(entry.url)}
                    style={{ position: 'absolute', bottom: 4, right: 4, background: 'rgba(0,0,0,0.6)', border: 'none', borderRadius: '50%', width: '22px', height: '22px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}
                    disabled={mediaLoading}
                    title="Hide"
                  >
                    <span className="material-icons" style={{ fontSize: '13px', color: 'white' }}>visibility</span>
                  </button>
                  <button onClick={() => setEditingCropIndex(i)} style={styles.imageEdit} disabled={mediaLoading}>
                    <span className="material-icons" style={{ fontSize: '14px' }}>crop</span>
                  </button>
                  <button onClick={() => deleteImage(i)} style={styles.imageRemove} disabled={mediaLoading}>×</button>
                </div>
              ))}
              {(vendor.hiddenImageUrls || []).map((entry) => (
                <div key={entry.url} style={{ position: 'relative' }}>
                  <CroppedImage entry={entry} alt="Hidden" style={styles.image} />
                  <div style={{
                    position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
                    backgroundColor: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'center',
                    justifyContent: 'center', textAlign: 'center', padding: '8px', borderRadius: 'inherit',
                  }}>
                    <span style={{ color: '#fff', fontSize: '11px', fontWeight: '700' }}>Hidden from Users</span>
                  </div>
                  <button
                    onClick={() => handleToggleImageHidden(entry.url)}
                    style={{ position: 'absolute', bottom: 4, right: 4, background: 'rgba(0,0,0,0.6)', border: 'none', borderRadius: '50%', width: '22px', height: '22px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}
                    disabled={mediaLoading}
                    title="Unhide"
                  >
                    <span className="material-icons" style={{ fontSize: '13px', color: 'white' }}>visibility_off</span>
                  </button>
                </div>
              ))}
              {(vendor.pendingImageUrls || []).map((entry, i) => (
                <div key={`pending-${i}`} style={{ position: 'relative' }}>
                  <CroppedImage entry={entry} alt={`Pending ${i + 1}`} style={styles.image} />
                  <div style={{
                    position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
                    backgroundColor: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'center',
                    justifyContent: 'center', textAlign: 'center', padding: '8px', borderRadius: 'inherit',
                  }}>
                    <span style={{ color: '#fff', fontSize: '11px', fontWeight: '700' }}>Needs User Approval</span>
                  </div>
                  <button onClick={() => handleDeletePendingImage(i)} style={styles.imageRemove} disabled={mediaLoading}>×</button>
                </div>
              ))}
            </div>
          )}
        </Section>
        {editingSocialMedia && (
          <AdminSocialMediaModal
            vendor={vendor}
            onClose={() => setEditingSocialMedia(false)}
            onSave={async (fields) => {
              await saveField(fields);
              setEditingSocialMedia(false);
            }}
          />
        )}
        {editingVendorSpecific && (
          <VendorSpecificModal
            vendor={vendor}
            onClose={() => setEditingVendorSpecific(false)}
            onSave={async (fields) => {
              await saveField(fields);
              setEditingVendorSpecific(false);
            }}
            requiresReApproval={false}
          />
        )}
        {editingServiceArea && (
          <AdminServiceAreaModal
            vendor={vendor}
            onClose={() => setEditingServiceArea(false)}
            onSave={async (fields) => {
              await saveField(fields);
              setEditingServiceArea(false);
            }}
          />
        )}
        {editingBusinessInfo && (
          <AdminBusinessInfoModal
            vendor={vendor}
            onClose={() => setEditingBusinessInfo(false)}
            onSave={async (fields) => {
              await saveField(fields);
              setEditingBusinessInfo(false);
            }}
          />
        )}
        {editingAccountContact && (
          <AdminAccountContactModal
            vendor={vendor}
            onClose={() => setEditingAccountContact(false)}
            onSave={async (fields) => {
              await saveField(fields);
              setEditingAccountContact(false);
            }}
          />
        )}
      </div>
      {showPreview && (
        <div style={previewStyles.overlay} onClick={() => setShowPreview(false)}>
          <div style={previewStyles.modal} onClick={(e) => e.stopPropagation()}>
            <div style={previewStyles.header}>
              <div>
                <span style={previewStyles.title}>Profile Preview</span>
                <p style={previewStyles.subtitle}>This is how couples see your profile</p>
              </div>
              <button onClick={() => setShowPreview(false)} style={previewStyles.closeButton}>×</button>
            </div>
            <div style={previewStyles.toggle}>
              <button onClick={() => setPreviewMode('full')} style={{ ...previewStyles.toggleBtn, ...(previewMode === 'full' ? previewStyles.toggleBtnActive : {}) }}>Full Profile</button>
              <button onClick={() => setPreviewMode('swipe')} style={{ ...previewStyles.toggleBtn, ...(previewMode === 'swipe' ? previewStyles.toggleBtnActive : {}) }}>Swipe Card</button>
            </div>

            {previewMode === 'swipe' && (
              <div style={previewStyles.swipeCard}>
                {vendor.reelUrl ? (
                  <video src={vendor.reelUrl} autoPlay muted loop style={previewStyles.swipeMedia} />
                ) : vendor.imageUrls?.[0] ? (
                  <CroppedImage entry={vendor.imageUrls[0]} alt="Profile" style={previewStyles.swipeMedia} />
                ) : (
                  <div style={{ ...previewStyles.mediaPlaceholder, height: '280px' }}>No media uploaded</div>
                )}
                <div style={previewStyles.reelLabel}>
                  <span className="material-icons" style={{ fontSize: '12px', color: 'white' }}>videocam</span>
                  <span style={{ fontSize: '12px', color: 'white', fontWeight: '500' }}>Intro Reel</span>
                </div>
                <div style={previewStyles.swipeInfo}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '6px' }}>
                    <span style={previewStyles.swipeName}>{vendor.companyName}</span>
                    {(vendor.differentiators || []).filter(d => d).length > 0 && (
                      <span style={previewStyles.swipeDiff}>{vendor.differentiators.filter(d => d).join(' | ')}</span>
                    )}
                  </div>
                  {vendor.businessAddress?.city && (
                    <p style={{ fontSize: '13px', color: '#888', margin: '4px 0 8px' }}>{vendor.businessAddress.city}, {vendor.businessAddress.state}</p>
                  )}
                  <span style={previewStyles.swipeBadge}>{vendor.vendorTypes?.[0]}</span>
                  {vendor.briefIntro && (
                    <>
                      <hr style={{ border: 'none', borderTop: '1px solid #eee', margin: '10px 0' }} />
                      <p style={{ fontSize: '14px', color: '#555', fontStyle: 'italic', lineHeight: 1.4, margin: 0 }}>"{vendor.briefIntro}"</p>
                    </>
                  )}
                  {SHOW_INSURANCE_FEATURE && vendor.hasInsurance && (
                    <div style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', marginTop: '10px', backgroundColor: 'rgba(0,0,0,0.06)', borderRadius: '8px', padding: '4px 10px' }}>
                      <span className="material-icons" style={{ fontSize: '14px', color: '#000' }}>verified</span>
                      <span style={{ fontSize: '12px', fontWeight: '700', color: '#000' }}>Fully Insured</span>
                    </div>
                  )}
                </div>
              </div>
            )}

            {previewMode === 'full' && (
              <>
                {(() => {
                  if (selectedMedia === 'reel' || (selectedMedia === null && vendor.reelUrl)) {
                    return <video src={vendor.reelUrl} autoPlay muted loop style={previewStyles.media} />;
                  } else if (selectedMedia) {
                    return <CroppedImage entry={selectedMedia} alt="Selected" style={previewStyles.media} />;
                  } else if (vendor.imageUrls?.[0]) {
                    return <CroppedImage entry={vendor.imageUrls[0]} alt="Profile" style={previewStyles.media} />;
                  } else {
                    return <div style={previewStyles.mediaPlaceholder}>No media uploaded</div>;
                  }
                })()}

                {(vendor.reelUrl || (vendor.imageUrls || []).length > 0) && (
                  <div style={previewStyles.gallery}>
                    {vendor.reelUrl && (
                      <div onClick={() => setSelectedMedia('reel')} style={{ ...previewStyles.thumbContainer, border: (selectedMedia === 'reel' || (selectedMedia === null && vendor.reelUrl)) ? '2px solid #000' : '2px solid transparent' }}>
                        <video src={vendor.reelUrl} style={previewStyles.thumb} muted />
                        <div style={previewStyles.reelOverlay}>▶</div>
                      </div>
                    )}
                    {(vendor.imageUrls || []).map((entry, i) => {
                      return (
                        <CroppedImage key={i} entry={entry} alt={`Gallery ${i + 1}`} onClick={() => setSelectedMedia(entry)}
                          style={{ ...previewStyles.thumb, border: selectedMedia === entry ? '2px solid #000' : '2px solid transparent', cursor: 'pointer', borderRadius: '6px' }} />
                      );
                    })}
                  </div>
                )}

                <div style={previewStyles.body}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
                    <h2 style={previewStyles.companyName}>{vendor.companyName}</h2>
                    {(vendor.differentiators || []).filter(d => d).length > 0 && (
                      <span style={previewStyles.differentiators}>{vendor.differentiators.filter(d => d).join(' | ')}</span>
                    )}
                  </div>
                  {vendor.businessAddress?.city && (
                    <p style={previewStyles.location}>{vendor.businessAddress.city}, {vendor.businessAddress.state}</p>
                  )}
                  <span style={previewStyles.typeBadge}>{vendor.vendorTypes?.[0]}</span>
                  <div style={{ margin: '12px 0 4px' }}>
                    {vendor.experienceYears > 0 && <p style={previewStyles.detailRow}><span className="material-icons" style={previewStyles.detailIcon}>workspace_premium</span> {vendor.experienceYears} years in business</p>}
                    {(vendor.languages || []).length > 0 && <p style={previewStyles.detailRow}><span className="material-icons" style={previewStyles.detailIcon}>language</span> {vendor.languages.join(', ')}</p>}
                    {(vendor.diversityTags || []).length > 0 && <p style={previewStyles.detailRow}><span className="material-icons" style={previewStyles.detailIcon}>diversity_3</span> {vendor.diversityTags.join(', ')}</p>}
                    {SHOW_INSURANCE_FEATURE && vendor.hasInsurance && <p style={previewStyles.detailRow}><span className="material-icons" style={previewStyles.detailIcon}>verified</span> Insured</p>}
                  </div>
                  {vendor.bio && (
                    <>
                      <p style={{ ...previewStyles.sectionTitle, marginTop: '24px' }}>About Us</p>
                      <p style={previewStyles.bio}>{vendor.bio}</p>
                    </>
                  )}
                  {(() => {
                    const safeStr = (value) => {
                      if (value == null) return '—';
                      if (Array.isArray(value)) return value.length === 0 ? '—' : value.join(', ');
                      if (value.toString().length === 0) return '—';
                      return value.toString();
                    };
                    return <div style={{ marginTop: '20px' }}><VendorSpecificDisplay vendor={vendor} safeString={safeStr} /></div>;
                  })()}
                  <div style={{ marginTop: '32px', textAlign: 'center' }}>
                    <p style={previewStyles.sectionTitle}>Contact Information</p>
                    {vendor.contactNamePublic && vendor.contactName && (
                      <p style={{ ...previewStyles.centeredRow, marginBottom: '12px' }}><span className="material-icons" style={previewStyles.detailIcon}>person</span> {vendor.contactName}</p>
                    )}
                    {vendor.phonePublic && vendor.phone && (
                      <p style={{ ...previewStyles.centeredRow, marginBottom: '12px' }}><span className="material-icons" style={previewStyles.detailIcon}>phone</span> {formatPhoneDisplay(vendor.phone)}</p>
                    )}
                    {vendor.email && (
                      <p style={{ ...previewStyles.centeredRow, marginBottom: '12px' }}><span className="material-icons" style={previewStyles.detailIcon}>email</span> {vendor.email}</p>
                    )}
                    {vendor.businessAddress && (
                      <div style={{ marginBottom: '12px', textAlign: 'center' }}>
                        <div style={{ ...previewStyles.centeredRow, gap: '6px' }}>
                          <span className="material-icons" style={previewStyles.detailIcon}>location_on</span>
                          <span style={{ fontSize: '14px', color: '#555' }}>{vendor.businessAddress.line1}{vendor.businessAddress.line2 ? `, ${vendor.businessAddress.line2}` : ''}</span>
                        </div>
                        <p style={{ fontSize: '14px', color: '#555', margin: '2px 0 0' }}>{vendor.businessAddress.city}, {vendor.businessAddress.state} {vendor.businessAddress.zip}</p>
                      </div>
                    )}
                  </div>
                  {(vendor.instagram || vendor.facebook || vendor.tiktok || vendor.pinterest || vendor.website) && (
                    <div style={{ marginTop: '20px', textAlign: 'center' }}>
                      <p style={previewStyles.sectionTitle}>Find Us Online</p>
                      {vendor.website && (
                        <p style={previewStyles.centeredRow}>
                          <span className="material-icons" style={previewStyles.detailIcon}>language</span>
                          <a href={vendor.website} target="_blank" rel="noreferrer" style={{ color: '#000', fontSize: '14px' }}>{vendor.website}</a>
                        </p>
                      )}
                      <div style={{ display: 'flex', justifyContent: 'center', gap: '24px', flexWrap: 'wrap', marginTop: '25px' }}>
                        {vendor.instagram && <a href={vendor.instagram} target="_blank" rel="noreferrer"><img src="https://cdn.simpleicons.org/instagram/000000" alt="Instagram" style={previewStyles.socialIcon} /></a>}
                        {vendor.facebook && <a href={vendor.facebook} target="_blank" rel="noreferrer"><img src="https://cdn.simpleicons.org/facebook/000000" alt="Facebook" style={previewStyles.socialIcon} /></a>}
                        {vendor.tiktok && <a href={vendor.tiktok} target="_blank" rel="noreferrer"><img src="https://cdn.simpleicons.org/tiktok/000000" alt="TikTok" style={previewStyles.socialIcon} /></a>}
                        {vendor.pinterest && <a href={vendor.pinterest} target="_blank" rel="noreferrer"><img src="https://cdn.simpleicons.org/pinterest/000000" alt="Pinterest" style={previewStyles.socialIcon} /></a>}
                      </div>
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      )}
      {showAuditLog && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.6)', zIndex: 1000, display: 'flex', justifyContent: 'center', alignItems: 'flex-start', overflowY: 'auto', padding: '40px 20px' }}
          onClick={() => setShowAuditLog(false)}>
          <div style={{ backgroundColor: 'white', borderRadius: '20px', width: '100%', maxWidth: '600px', maxHeight: '80vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}
            onClick={(e) => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '24px 24px 16px', borderBottom: '1px solid #f0f0f0' }}>
              <h2 style={{ fontSize: '18px', fontWeight: '800', color: '#000', margin: 0 }}>Audit Log</h2>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <button onClick={handleDownloadAuditLog} title="Download as text file" style={{ background: 'none', border: '1px solid #ddd', borderRadius: '8px', padding: '6px 10px', cursor: 'pointer', color: '#000', fontSize: '13px', fontWeight: '600' }}>
                  Download
                </button>
                <button onClick={handlePrintAuditLog} title="Print" style={{ background: 'none', border: '1px solid #ddd', borderRadius: '8px', padding: '6px 10px', cursor: 'pointer', color: '#000', fontSize: '13px', fontWeight: '600' }}>
                  Print
                </button>
                <button onClick={() => setShowAuditLog(false)} style={{ background: 'none', border: 'none', fontSize: '24px', cursor: 'pointer', color: '#888' }}>×</button>
              </div>
            </div>
            <div style={{ overflowY: 'auto', padding: '16px 24px', flex: 1 }}>
              {auditLoading ? (
                <p style={{ color: '#888', textAlign: 'center' }}>Loading...</p>
              ) : auditEntries.length === 0 ? (
                <p style={{ color: '#888', textAlign: 'center' }}>No changes recorded yet.</p>
              ) : (
                auditEntries.map((entry) => {
                  const changedBy = entry.changedBy || '—';
                  const changedByName = entry.changedByName;
                  const changes = entry.changes || [];
                  const formatTime = (ts) => {
                    if (!ts) return '—';
                    try {
                      const dt = ts.toDate ? ts.toDate() : new Date(ts.seconds * 1000);
                      const period = dt.getHours() >= 12 ? 'PM' : 'AM';
                      const hour = dt.getHours() > 12 ? dt.getHours() - 12 : dt.getHours() === 0 ? 12 : dt.getHours();
                      return `${dt.getMonth() + 1}/${dt.getDate()}/${dt.getFullYear()} ${hour}:${dt.getMinutes().toString().padStart(2, '0')} ${period}`;
                    } catch { return '—'; }
                  };
                  return (
                    <div key={entry.id} style={{ backgroundColor: '#F5F5EF', borderRadius: '12px', padding: '16px', marginBottom: '12px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px' }}>
                        <span style={{
                          fontSize: '11px', fontWeight: '700', padding: '3px 8px', borderRadius: '6px',
                          backgroundColor: changedBy === 'admin' ? 'rgba(44,62,80,0.1)' : changedBy === 'report' ? 'rgba(185,28,28,0.1)' : 'rgba(76,175,80,0.1)',
                          color: changedBy === 'admin' ? '#2C3E50' : changedBy === 'report' ? '#B91C1C' : '#4CAF50',
                        }}>
                          {changedBy === 'admin'
                            ? (changedByName ? `Admin - ${changedByName}` : 'Admin')
                            : changedBy === 'report'
                              ? (changedByName ? `Reported by ${changedByName}` : 'Reported')
                              : 'Vendor'}
                        </span>
                        <span style={{ fontSize: '11px', color: '#aaa' }}>{formatTime(entry.changedAt)}</span>
                      </div>
                      {changes.map((change, i) => (
                        <div key={i} style={{ marginBottom: '6px' }}>
                          <div style={{ fontSize: '12px', fontWeight: '700', color: '#2D2D2D', marginBottom: '2px' }}>{change.field}</div>
                          <div style={{ fontSize: '12px', display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                            <span style={{ color: '#E53935', textDecoration: 'line-through' }}>{change.from}</span>
                            <span style={{ color: '#aaa' }}>→</span>
                            <span style={{ color: '#4CAF50', fontWeight: '600' }}>{change.to}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </div>

      )}
      {showMediaRightsLog && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.6)', zIndex: 1000, display: 'flex', justifyContent: 'center', alignItems: 'flex-start', overflowY: 'auto', padding: '40px 20px' }}
          onClick={() => setShowMediaRightsLog(false)}>
          <div style={{ backgroundColor: 'white', borderRadius: '20px', width: '100%', maxWidth: '600px', maxHeight: '80vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}
            onClick={(e) => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '24px 24px 16px', borderBottom: '1px solid #f0f0f0' }}>
              <h2 style={{ fontSize: '18px', fontWeight: '800', color: '#000', margin: 0 }}>Media Rights Log</h2>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <button onClick={handleDownloadMediaRightsLog} title="Download as text file" style={{ background: 'none', border: '1px solid #ddd', borderRadius: '8px', padding: '6px 10px', cursor: 'pointer', color: '#000', fontSize: '13px', fontWeight: '600' }}>
                  Download
                </button>
                <button onClick={handlePrintMediaRightsLog} title="Print" style={{ background: 'none', border: '1px solid #ddd', borderRadius: '8px', padding: '6px 10px', cursor: 'pointer', color: '#000', fontSize: '13px', fontWeight: '600' }}>
                  Print
                </button>
                <button onClick={() => setShowMediaRightsLog(false)} style={{ background: 'none', border: 'none', fontSize: '24px', cursor: 'pointer', color: '#888' }}>×</button>
              </div>
            </div>
            <div style={{ overflowY: 'auto', padding: '16px 24px', flex: 1 }}>
              {mediaRightsLoading ? (
                <p style={{ color: '#888', textAlign: 'center' }}>Loading...</p>
              ) : mediaRightsEntries.length === 0 ? (
                <p style={{ color: '#888', textAlign: 'center' }}>No confirmations recorded yet.</p>
              ) : (
                mediaRightsEntries.map((entry) => {
                  const formatTime = (ts) => {
                    if (!ts) return '—';
                    try {
                      const dt = ts.toDate ? ts.toDate() : new Date(ts.seconds * 1000);
                      return dt.toLocaleString();
                    } catch { return '—'; }
                  };
                  return (
                    <div key={entry.id} style={{ backgroundColor: '#F5F5EF', borderRadius: '12px', padding: '16px', marginBottom: '12px' }}>
                      <div style={{ fontSize: '11px', color: '#aaa', marginBottom: '8px' }}>{formatTime(entry.timestamp)}</div>
                      <div style={{ fontSize: '12px', marginBottom: '4px' }}><strong>User:</strong> {entry.userId}</div>
                      <div style={{ fontSize: '12px', marginBottom: '4px', wordBreak: 'break-all' }}><strong>Media:</strong> {entry.mediaId}</div>
                      <div style={{ fontSize: '12px', marginBottom: '4px' }}><strong>Terms Version:</strong> {entry.termsVersion}</div>
                      <div style={{ fontSize: '12px', marginBottom: '4px' }}><strong>IP:</strong> {entry.ipAddress || '—'}</div>
                      <div style={{ fontSize: '12px' }}><strong>Device:</strong> {entry.deviceInfo || '—'}</div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </div>
      )}
      {showReportLog && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.6)', zIndex: 1000, display: 'flex', justifyContent: 'center', alignItems: 'flex-start', overflowY: 'auto', padding: '40px 20px' }}
          onClick={() => setShowReportLog(false)}>
          <div style={{ backgroundColor: 'white', borderRadius: '20px', width: '100%', maxWidth: '600px', maxHeight: '80vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}
            onClick={(e) => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '24px 24px 16px', borderBottom: '1px solid #f0f0f0' }}>
              <h2 style={{ fontSize: '18px', fontWeight: '800', color: '#000', margin: 0 }}>Report Log</h2>
              <button onClick={() => setShowReportLog(false)} style={{ background: 'none', border: 'none', fontSize: '24px', cursor: 'pointer', color: '#888' }}>×</button>
            </div>
            <div style={{ overflowY: 'auto', padding: '16px 24px', flex: 1 }}>
              {reportLogLoading ? (
                <p style={{ color: '#888', textAlign: 'center' }}>Loading...</p>
              ) : reportLogEntries.length === 0 ? (
                <p style={{ color: '#888', textAlign: 'center' }}>No reports on record.</p>
              ) : (
                reportLogEntries.map((entry) => {
                  const formatTime = (ts) => {
                    if (!ts) return '—';
                    try {
                      const dt = ts.toDate ? ts.toDate() : new Date(ts.seconds * 1000);
                      const period = dt.getHours() >= 12 ? 'PM' : 'AM';
                      const hour = dt.getHours() > 12 ? dt.getHours() - 12 : dt.getHours() === 0 ? 12 : dt.getHours();
                      return `${dt.getMonth() + 1}/${dt.getDate()}/${dt.getFullYear()} ${hour}:${dt.getMinutes().toString().padStart(2, '0')} ${period}`;
                    } catch { return '—'; }
                  };
                  return (
                    <div key={entry.id} style={{ backgroundColor: '#F5F5EF', borderRadius: '12px', padding: '16px', marginBottom: '12px' }}>
                      <div style={{ fontSize: '13px', fontWeight: '700', color: '#FF5722', marginBottom: 4 }}>
                        {entry.reason === 'Other' && entry.otherReason ? `Other: ${entry.otherReason}` : entry.reason}
                      </div>
                      {entry.description && (
                        <div style={{ fontSize: '12px', color: '#555', marginBottom: 4 }}>{entry.description}</div>
                      )}
                      <div style={{ fontSize: '11px', color: '#aaa' }}>
                        Reported by: {entry.userEmail}{entry.createdAt ? ` · ${formatTime(entry.createdAt)}` : ''}
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </div>
      )}
      {croppingFile && (
        <ImageCropModal
          imageSrc={croppingFile.previewUrl}
          onCancel={handleCropCancel}
          onConfirm={handleCropConfirm}
        />
      )}
      {confirmingDeleteIndex !== null && (
        <ConfirmModal
          message="Delete this image?"
          onCancel={() => setConfirmingDeleteIndex(null)}
          onConfirm={handleConfirmDeleteImage}
        />
      )}
      {confirmingApprove && (
        <ConfirmModal
          message="Approve this vendor?"
          onCancel={() => setConfirmingApprove(false)}
          onConfirm={handleConfirmApprove}
        />
      )}
      {confirmingReject && (
        <PromptModal
          message="Enter rejection reason:"
          placeholder="Reason for rejection..."
          onCancel={() => setConfirmingReject(false)}
          onConfirm={handleConfirmReject}
        />
      )}
      {editingCropIndex !== null && (
        <ImageCropModal
          imageSrc={getImageUrl(vendor.imageUrls[editingCropIndex])}
          initialCrop={
            typeof vendor.imageUrls[editingCropIndex] === 'object'
              ? { x: vendor.imageUrls[editingCropIndex].panX ?? 0, y: vendor.imageUrls[editingCropIndex].panY ?? 0 }
              : undefined
          }
          initialZoom={
            typeof vendor.imageUrls[editingCropIndex] === 'object'
              ? vendor.imageUrls[editingCropIndex].zoom ?? 1
              : undefined
          }
          onCancel={() => setEditingCropIndex(null)}
          onConfirm={handleEditCropConfirm}
        />
      )}
    </div>

  );
}
function AdminSocialMediaModal({ vendor, onClose, onSave }) {
  const [instagram, setInstagram] = useState(vendor.instagram || '');
  const [facebook, setFacebook] = useState(vendor.facebook || '');
  const [tiktok, setTiktok] = useState(vendor.tiktok || '');
  const [pinterest, setPinterest] = useState(vendor.pinterest || '');
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    await onSave({
      instagram: instagram.trim(),
      facebook: facebook.trim(),
      tiktok: tiktok.trim(),
      pinterest: pinterest.trim(),
    });
    setSaving(false);
  };

  return (
    <div style={modalStyles.overlay} data-modal-overlay="true">
      <div style={modalStyles.modal}>
        <div style={modalStyles.modalHeader}>
          <span style={modalStyles.modalTitle}>Edit Social Media</span>
          <button onClick={onClose} style={modalStyles.closeButton}>×</button>
        </div>

        <div style={modalStyles.field}>
          <label style={modalStyles.label}>Instagram</label>
          <input value={instagram} onChange={(e) => setInstagram(e.target.value)} style={modalStyles.input} />
        </div>
        <div style={modalStyles.field}>
          <label style={modalStyles.label}>Facebook</label>
          <input value={facebook} onChange={(e) => setFacebook(e.target.value)} style={modalStyles.input} />
        </div>
        <div style={modalStyles.field}>
          <label style={modalStyles.label}>TikTok</label>
          <input value={tiktok} onChange={(e) => setTiktok(e.target.value)} style={modalStyles.input} />
        </div>
        <div style={modalStyles.field}>
          <label style={modalStyles.label}>Pinterest</label>
          <input value={pinterest} onChange={(e) => setPinterest(e.target.value)} style={modalStyles.input} />
        </div>

        <button onClick={handleSave} style={modalStyles.saveButton} disabled={saving}>
          {saving ? 'Saving...' : 'Save Changes'}
        </button>
      </div>
    </div>
  );
}

function AdminServiceAreaModal({ vendor, onClose, onSave }) {
  const isExact = vendor.vendorTypes?.[0] === 'Venue' || vendor.vendorTypes?.[0] === 'Wedding Attire';
  const RADIUS_OPTIONS = [25, 50, 75, 100];

  const [serviceRadius, setServiceRadius] = useState(
    RADIUS_OPTIONS.includes(vendor.serviceRadiusMiles) ? vendor.serviceRadiusMiles : 25
  );
  const [useCustomRadius, setUseCustomRadius] = useState(
    vendor.serviceRadiusMiles && !RADIUS_OPTIONS.includes(vendor.serviceRadiusMiles)
  );
  const [customRadius, setCustomRadius] = useState(
    vendor.serviceRadiusMiles && !RADIUS_OPTIONS.includes(vendor.serviceRadiusMiles) ? vendor.serviceRadiusMiles.toString() : ''
  );
  const [additionalCities, setAdditionalCities] = useState(vendor.additionalCities || []);
  const [newCity, setNewCity] = useState('');
  const [cityMarkers, setCityMarkers] = useState([]);
  const [saving, setSaving] = useState(false);

  const currentRadius = useCustomRadius ? (parseInt(customRadius) || 0) : serviceRadius;

  const geocodeCity = async (cityState) => {
    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(cityState)}&format=json&limit=1`
      );
      const data = await response.json();
      if (data && data.length > 0) {
        return { latitude: parseFloat(data[0].lat), longitude: parseFloat(data[0].lon), name: cityState };
      }
    } catch (err) {
      console.error(err);
    }
    return null;
  };

  const addCity = async () => {
    const trimmed = newCity.trim();
    if (trimmed && !additionalCities.includes(trimmed)) {
      setAdditionalCities([...additionalCities, trimmed]);
      setNewCity('');
      const coords = await geocodeCity(trimmed);
      if (coords) setCityMarkers((prev) => [...prev, coords]);
    }
  };

  const removeCity = (c) => {
    setAdditionalCities(additionalCities.filter((city) => city !== c));
    setCityMarkers(cityMarkers.filter((m) => m.name !== c));
  };

  const handleSave = async () => {
    setSaving(true);
    const coords = [];
    for (const city of additionalCities) {
      const c = await geocodeCity(city);
      if (c) coords.push({ latitude: c.latitude, longitude: c.longitude });
    }
    const fields = {
      serviceRadiusMiles: isExact ? 0 : currentRadius,
      additionalCities: isExact ? [] : additionalCities,
      additionalCitiesCoords: isExact ? [] : coords,
    };
    await onSave({
      ...fields,
      ...computeServiceAreaStatusFields(vendor, fields),
    });
    setSaving(false);
  };

  return (
    <div style={modalStyles.overlay} data-modal-overlay="true">
      <div style={modalStyles.modal}>
        <div style={modalStyles.modalHeader}>
          <span style={modalStyles.modalTitle}>Edit Service Area</span>
          <button onClick={onClose} style={modalStyles.closeButton}>×</button>
        </div>

        {vendor.latitude && vendor.longitude && (
          <div style={{ marginBottom: '20px', borderRadius: '12px', overflow: 'hidden', position: 'relative', zIndex: 0 }}>
            <MapContainer
              center={[vendor.latitude, vendor.longitude]}
              zoom={isExact ? 13 : getZoomForRadius(currentRadius)}
              style={{ height: '220px', width: '100%' }}
              key={`${vendor.latitude}-${vendor.longitude}-${currentRadius}`}
            >
              <TileLayer
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
              />
              <Marker position={[vendor.latitude, vendor.longitude]} />
              {!isExact && currentRadius > 0 && (
                <Circle
                  center={[vendor.latitude, vendor.longitude]}
                  radius={currentRadius * 1609.34}
                  pathOptions={{ color: '#000000', fillColor: '#000000', fillOpacity: 0.1 }}
                />
              )}
              {cityMarkers.map((m, i) => (
                <Marker key={i} position={[m.latitude, m.longitude]} />
              ))}
            </MapContainer>
          </div>
        )}

        {isExact ? (
          <p style={{ fontSize: '13px', color: '#888', marginBottom: '20px' }}>
            This vendor type uses their exact business address as their location.
          </p>
        ) : (
          <>
            <div style={modalStyles.field}>
              <label style={modalStyles.label}>Service Radius</label>
              <div style={modalStyles.chipRow}>
                {RADIUS_OPTIONS.map((miles) => (
                  <button key={miles} type="button" onMouseDown={(e) => e.preventDefault()}
                    onClick={() => { setServiceRadius(miles); setUseCustomRadius(false); setCustomRadius(''); }}
                    style={{ ...modalStyles.chip, ...(!useCustomRadius && serviceRadius === miles ? modalStyles.chipSelected : {}) }}>
                    {miles} miles
                  </button>
                ))}
                <button type="button" onMouseDown={(e) => e.preventDefault()}
                  onClick={() => setUseCustomRadius(true)}
                  style={{ ...modalStyles.chip, ...(useCustomRadius ? modalStyles.chipSelected : {}) }}>
                  Custom
                </button>
              </div>
              {useCustomRadius && (
                <input
                  type="text"
                  inputMode="numeric"
                  value={customRadius}
                  onChange={(e) => setCustomRadius(e.target.value.replace(/\D/g, '').slice(0, 3))}
                  style={{ ...modalStyles.input, marginTop: '12px' }}
                  placeholder="Custom radius (miles)"
                />
              )}
            </div>

            <div style={modalStyles.field}>
              <label style={modalStyles.label}>Additional Cities</label>
              <div style={{ display: 'flex', gap: '8px' }}>
                <CityAutocomplete
                  value={newCity}
                  onChange={setNewCity}
                  placeholder="City, State"
                  style={{ ...modalStyles.input, flex: 1 }}
                />
                <button type="button" onClick={addCity} style={{ padding: '12px 18px', backgroundColor: '#000000', color: 'white', border: 'none', borderRadius: '10px', fontWeight: '700', cursor: 'pointer' }}>
                  Add
                </button>
              </div>
              {additionalCities.length > 0 && (
                <div style={{ ...modalStyles.chipRow, marginTop: '10px' }}>
                  {additionalCities.map((c) => (
                    <div key={c} style={{ ...modalStyles.chip, display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <span>{c}</span>
                      <span onClick={() => removeCity(c)} style={{ cursor: 'pointer' }}>×</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
        )}

        <button onClick={handleSave} style={modalStyles.saveButton} disabled={saving}>
          {saving ? 'Saving...' : 'Save Changes'}
        </button>
      </div>
    </div>
  );
}

function AdminBusinessInfoModal({ vendor, onClose, onSave }) {
  const ALL_LANGUAGES = ['English', 'Spanish', 'French', 'Mandarin', 'Cantonese', 'Japanese', 'Korean', 'Vietnamese', 'Tagalog', 'Arabic', 'Hindi', 'Portuguese', 'Italian', 'German', 'Russian', 'Other'];
  const ALL_DIVERSITY_TAGS = ['Asian-Owned', 'Black-Owned', 'Hispanic/Latinx-Owned', 'LGBTQ+', 'Native American-Owned', 'Pacific Islander-Owned', 'Veteran-Owned', 'Women-Owned'];

  const [phonePublic, setPhonePublic] = useState(vendor.phonePublic || false);
  const [contactNamePublic, setContactNamePublic] = useState(vendor.contactNamePublic || false);
  const [diff1, setDiff1] = useState(vendor.differentiators?.[0] || '');
  const [diff2, setDiff2] = useState(vendor.differentiators?.[1] || '');
  const [diff3, setDiff3] = useState(vendor.differentiators?.[2] || '');
  const [briefIntro, setBriefIntro] = useState(vendor.briefIntro || '');
  const [bio, setBio] = useState(vendor.bio || '');
  const knownLangs = ['English', 'Spanish', 'French', 'Mandarin', 'Cantonese', 'Japanese', 'Korean', 'Vietnamese', 'Tagalog', 'Arabic', 'Hindi', 'Portuguese', 'Italian', 'German', 'Russian'];
  const initialLanguages = vendor.languages || [];
  const detectedOther = initialLanguages.find((l) => !knownLangs.includes(l));
  const [languages, setLanguages] = useState(detectedOther ? [...initialLanguages.filter((l) => l !== detectedOther), 'Other'] : initialLanguages);
  const [otherLanguage, setOtherLanguage] = useState(detectedOther || '');
  const [diversityTags, setDiversityTags] = useState(vendor.diversityTags || []);
  const [experienceYear, setExperienceYear] = useState(vendor.experienceYear ? vendor.experienceYear.toString() : '');
  const [hasInsurance, setHasInsurance] = useState(vendor.hasInsurance || false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const toggle = (value, array, setArray) => {
    if (array.includes(value)) setArray(array.filter((v) => v !== value));
    else setArray([...array, value]);
  };

  const handleSave = async () => {
    if (!diff1.trim() || !diff2.trim() || !diff3.trim()) { scrollModalToTop(); setError('Please fill in all 3 differentiators.'); return; }
    if (!briefIntro.trim()) { scrollModalToTop(); setError('Brief intro is required.'); return; }
    if (!bio.trim()) { scrollModalToTop(); setError('Bio is required.'); return; }
    setSaving(true);
    scrollModalToTop(); setError('');
    const finalLanguages = languages.includes('Other') && otherLanguage.trim()
      ? [...languages.filter((l) => l !== 'Other'), otherLanguage.trim()]
      : languages;
    await onSave({
      phonePublic,
      contactNamePublic,
      differentiators: [diff1.trim(), diff2.trim(), diff3.trim()],
      briefIntro: briefIntro.trim(),
      bio: bio.trim(),
      languages: finalLanguages,
      diversityTags,
      experienceYear: experienceYear ? parseInt(experienceYear) : 0,
      experienceYears: experienceYear ? (new Date().getFullYear() - parseInt(experienceYear)) : 0,
      hasInsurance,
    });
    setSaving(false);
  };

  return (
    <div style={modalStyles.overlay} data-modal-overlay="true">
      <div style={modalStyles.modal}>
        <div style={modalStyles.modalHeader}>
          <span style={modalStyles.modalTitle}>Edit Business Information</span>
          <button onClick={onClose} style={modalStyles.closeButton}>×</button>
        </div>

        {error && <p style={{ color: '#e53935', fontSize: '13px', marginBottom: '12px' }}>{error}</p>}

        <div style={modalStyles.checkboxRow}>
          <input type="checkbox" checked={phonePublic} onChange={(e) => setPhonePublic(e.target.checked)} id="adminPhonePublic" />
          <label htmlFor="adminPhonePublic" style={modalStyles.checkboxLabel}>Show phone number publicly</label>
        </div>
        <div style={modalStyles.checkboxRow}>
          <input type="checkbox" checked={contactNamePublic} onChange={(e) => setContactNamePublic(e.target.checked)} id="adminContactPublic" />
          <label htmlFor="adminContactPublic" style={modalStyles.checkboxLabel}>Show contact name publicly</label>
        </div>

        <div style={modalStyles.field}>
          <label style={modalStyles.label}>Differentiator 1 *</label>
          <input value={diff1} onChange={(e) => setDiff1(e.target.value.slice(0, 20))} style={modalStyles.input} maxLength={20} />
        </div>
        <div style={modalStyles.field}>
          <label style={modalStyles.label}>Differentiator 2 *</label>
          <input value={diff2} onChange={(e) => setDiff2(e.target.value.slice(0, 20))} style={modalStyles.input} maxLength={20} />
        </div>
        <div style={modalStyles.field}>
          <label style={modalStyles.label}>Differentiator 3 *</label>
          <input value={diff3} onChange={(e) => setDiff3(e.target.value.slice(0, 20))} style={modalStyles.input} maxLength={20} />
        </div>

        <div style={modalStyles.field}>
          <label style={modalStyles.label}>Brief Intro * (160 chars max)</label>
          <textarea value={briefIntro} onChange={(e) => setBriefIntro(e.target.value.slice(0, 160))} style={{ ...modalStyles.input, minHeight: '70px' }} maxLength={160} />
        </div>

        <div style={modalStyles.field}>
          <label style={modalStyles.label}>Bio *</label>
          <textarea value={bio} onChange={(e) => setBio(e.target.value)} style={{ ...modalStyles.input, minHeight: '100px' }} />
        </div>

        <div style={modalStyles.field}>
          <label style={modalStyles.label}>Languages</label>
          <div style={modalStyles.chipRow}>
            {ALL_LANGUAGES.map((lang) => (
              <button key={lang} type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => toggle(lang, languages, setLanguages)}
                style={{ ...modalStyles.chip, ...(languages.includes(lang) ? modalStyles.chipSelected : {}) }}>
                {lang}
              </button>
            ))}
          </div>
          {languages.includes('Other') && (
            <input value={otherLanguage} onChange={(e) => setOtherLanguage(e.target.value.slice(0, 15))} style={{ ...modalStyles.input, marginTop: '12px' }} placeholder="Specify other language" maxLength={15} />
          )}
        </div>

        <div style={modalStyles.field}>
          <label style={modalStyles.label}>Diversity Tags</label>
          <div style={modalStyles.chipRow}>
            {ALL_DIVERSITY_TAGS.map((tag) => (
              <button key={tag} type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => toggle(tag, diversityTags, setDiversityTags)}
                style={{ ...modalStyles.chip, ...(diversityTags.includes(tag) ? modalStyles.chipSelected : {}) }}>
                {tag}
              </button>
            ))}
          </div>
        </div>

        <div style={modalStyles.field}>
          <label style={modalStyles.label}>Year Started in Business</label>
          <div style={{ display: 'flex', gap: '12px' }}>
            <select value={experienceYear} onChange={(e) => setExperienceYear(e.target.value)} style={{ ...modalStyles.input, flex: 2 }}>
              <option value="">Not specified</option>
              {Array.from({ length: new Date().getFullYear() - 1900 + 1 }, (_, i) => new Date().getFullYear() - i).map((y) => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
            {experienceYear && (
              <div style={{ flex: 3, display: 'flex', alignItems: 'center', backgroundColor: '#F5F5EF', borderRadius: '10px', padding: '0 14px' }}>
                <span style={{ fontSize: '13px', fontWeight: '600', color: '#000000' }}>
                  {new Date().getFullYear() - parseInt(experienceYear)} years in business
                </span>
              </div>
            )}
          </div>
        </div>

        {SHOW_INSURANCE_FEATURE && (
          <div style={modalStyles.checkboxRow}>
            <input type="checkbox" checked={hasInsurance} onChange={(e) => setHasInsurance(e.target.checked)} id="adminHasInsurance" />
            <label htmlFor="adminHasInsurance" style={modalStyles.checkboxLabel}>Has Insurance</label>
          </div>
        )}

        <button onClick={handleSave} style={modalStyles.saveButton} disabled={saving}>
          {saving ? 'Saving...' : 'Save Changes'}
        </button>
      </div>
    </div>
  );
}

function AdminAccountContactModal({ vendor, onClose, onSave }) {
  const [companyName, setCompanyName] = useState(vendor.companyName || '');
  const [contactName, setContactName] = useState(vendor.contactName || '');
  const [email, setEmail] = useState(vendor.email || '');
  const [website, setWebsite] = useState(vendor.website || '');
  const [phone, setPhone] = useState(vendor.phone || '');
  const [vendorType, setVendorType] = useState(vendor.vendorTypes?.[0] || '');
  const [addressLine1, setAddressLine1] = useState(vendor.businessAddress?.line1 || '');
  const [addressLine2, setAddressLine2] = useState(vendor.businessAddress?.line2 || '');
  const [city, setCity] = useState(vendor.businessAddress?.city || '');
  const [state, setState] = useState(vendor.businessAddress?.state || '');
  const [zip, setZip] = useState(vendor.businessAddress?.zip || '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const handleSave = async () => {
    if (!companyName.trim()) { scrollModalToTop(); setError('Company name is required.'); return; }
    if (!contactName.trim()) { scrollModalToTop(); setError('Contact name is required.'); return; }
    if (!email.trim()) { scrollModalToTop(); setError('Email is required.'); return; }
    if (phone.replace(/\D/g, '').length !== 10) { scrollModalToTop(); setError('Please enter a valid 10-digit phone number.'); return; }
    if (!vendorType) { scrollModalToTop(); setError('Vendor type is required.'); return; }
    if (!addressLine1.trim() || !city.trim() || !state || !zip.trim()) { scrollModalToTop(); setError('Please fill in all required address fields.'); return; }
    setSaving(true);
    scrollModalToTop(); setError('');
    await onSave({
      companyName: companyName.trim(),
      contactName: contactName.trim(),
      email: email.trim(),
      website: website.trim(),
      phone: phone.replace(/\D/g, ''),
      vendorTypes: [vendorType],
      businessAddress: {
        line1: addressLine1.trim(),
        line2: addressLine2.trim(),
        city: city.trim(),
        state,
        zip: zip.trim(),
      },
      location: `${city.trim()}, ${state}`,
    });
    setSaving(false);
  };

  return (
    <div style={modalStyles.overlay} data-modal-overlay="true">
      <div style={modalStyles.modal}>
        <div style={modalStyles.modalHeader}>
          <span style={modalStyles.modalTitle}>Edit Account & Contact</span>
          <button onClick={onClose} style={modalStyles.closeButton}>×</button>
        </div>

        {error && <p style={{ color: '#e53935', fontSize: '13px', marginBottom: '12px' }}>{error}</p>}

        <div style={modalStyles.field}>
          <label style={modalStyles.label}>Company Name *</label>
          <input value={companyName} onChange={(e) => setCompanyName(e.target.value)} style={modalStyles.input} />
        </div>
        <div style={modalStyles.field}>
          <label style={modalStyles.label}>Contact Name *</label>
          <input value={contactName} onChange={(e) => setContactName(e.target.value)} style={modalStyles.input} />
        </div>
        <div style={modalStyles.field}>
          <label style={modalStyles.label}>Email *</label>
          <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} style={modalStyles.input} />
        </div>
        <div style={modalStyles.field}>
          <label style={modalStyles.label}>Website</label>
          <input value={website} onChange={(e) => setWebsite(e.target.value)} style={modalStyles.input} placeholder="https://" />
        </div>
        <div style={modalStyles.field}>
          <label style={modalStyles.label}>Phone *</label>
          <input
            value={formatPhoneInput(phone)}
            onChange={(e) => setPhone(e.target.value.replace(/\D/g, '').slice(0, 10))}
            style={modalStyles.input}
            placeholder="(555) 555-5555"
          />
        </div>
        <div style={modalStyles.field}>
          <label style={modalStyles.label}>Vendor Type *</label>
          <select value={vendorType} onChange={(e) => setVendorType(e.target.value)} style={modalStyles.input}>
            <option value="">Select...</option>
            {ALL_VENDOR_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
        </div>
        <div style={modalStyles.field}>
          <label style={modalStyles.label}>Address Line 1 *</label>
          <input value={addressLine1} onChange={(e) => setAddressLine1(e.target.value)} style={modalStyles.input} />
        </div>
        <div style={modalStyles.field}>
          <label style={modalStyles.label}>Address Line 2 (optional)</label>
          <input value={addressLine2} onChange={(e) => setAddressLine2(e.target.value)} style={modalStyles.input} />
        </div>
        <div style={{ display: 'flex', gap: '12px' }}>
          <div style={{ ...modalStyles.field, flex: 2 }}>
            <label style={modalStyles.label}>City *</label>
            <CityAutocomplete
              value={city}
              onChange={setCity}
              onCitySelected={(cityName, matchedState) => {
                setCity(cityName);
                if (!state) setState(matchedState);
              }}
              stateFilter={state}
              style={modalStyles.input}
            />
          </div>
          <div style={{ ...modalStyles.field, flex: 1 }}>
            <label style={modalStyles.label}>State *</label>
            <select value={state} onChange={(e) => setState(e.target.value)} style={modalStyles.input}>
              <option value="">--</option>
              {US_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <div style={{ ...modalStyles.field, flex: 1 }}>
            <label style={modalStyles.label}>Zip *</label>
            <input value={zip} onChange={(e) => setZip(e.target.value.replace(/\D/g, '').slice(0, 5))} style={modalStyles.input} />
          </div>
        </div>

        <button onClick={handleSave} style={modalStyles.saveButton} disabled={saving}>
          {saving ? 'Saving...' : 'Save Changes'}
        </button>
      </div>
    </div>
  );
}

function FlaggedReportsBanner({ vendorId }) {
  const [reports, setReports] = React.useState([]);

  React.useEffect(() => {
    const fetchReports = async () => {
      const { collection, query, where, orderBy, getDocs } = await import('firebase/firestore');
      const { db } = await import('../firebase');
      const q = query(
        collection(db, 'reports'),
        where('vendorId', '==', vendorId),
        orderBy('createdAt', 'desc')
      );
      const snap = await getDocs(q);
      setReports(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    };
    fetchReports();
  }, [vendorId]);

  if (reports.length === 0) return null;

  const formatTime = (ts) => {
    if (!ts) return '';
    try {
      const dt = ts.toDate ? ts.toDate() : new Date(ts.seconds * 1000);
      const period = dt.getHours() >= 12 ? 'PM' : 'AM';
      const hour = dt.getHours() > 12 ? dt.getHours() - 12 : dt.getHours() === 0 ? 12 : dt.getHours();
      return `${dt.getMonth() + 1}/${dt.getDate()}/${dt.getFullYear()} ${hour}:${dt.getMinutes().toString().padStart(2, '0')} ${period}`;
    } catch { return ''; }
  };

  return (
    <div style={{ backgroundColor: 'white', borderRadius: '16px', padding: '24px', marginBottom: '20px', boxShadow: '0 2px 12px rgba(0,0,0,0.06)', borderLeft: '4px solid #FF5722' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
        <span style={{ fontSize: '16px' }}>🚩</span>
        <h2 style={{ fontSize: '16px', fontWeight: '700', color: '#FF5722', margin: 0 }}>
          {reports.length} Report{reports.length === 1 ? '' : 's'}
        </h2>
      </div>
      {reports.map((r) => (
        <div key={r.id} style={{ backgroundColor: '#FFF3F0', borderRadius: '10px', padding: '12px', marginBottom: '8px' }}>
          <div style={{ fontSize: '13px', fontWeight: '700', color: '#FF5722' }}>
            {r.reason === 'Other' && r.otherReason ? `Other: ${r.otherReason}` : r.reason}
          </div>
          {r.description && (
            <div style={{ fontSize: '13px', color: '#555', marginTop: '4px' }}>{r.description}</div>
          )}
          <div style={{ fontSize: '11px', color: '#aaa', marginTop: '4px' }}>
            Reported by: {r.userEmail}{r.createdAt ? ` · ${formatTime(r.createdAt)}` : ''}
          </div>
        </div>
      ))}
    </div>
  );
}

function Section({ title, onEdit, children }) {
  return (
    <div style={styles.section}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', paddingBottom: '12px', borderBottom: '1px solid #f0f0f0' }}>
        <h2 style={{ ...styles.sectionTitle, marginBottom: 0, paddingBottom: 0, borderBottom: 'none' }}>{title}</h2>
        {onEdit && <button onClick={onEdit} style={styles.editButton}>✏️ Edit</button>}
      </div>
      <div style={styles.sectionContent}>
        {children}
      </div>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div style={styles.row}>
      <span style={styles.rowLabel}>{label}</span>
      <span style={styles.rowValue}>{value}</span>
    </div>
  );
}



const previewStyles = {
  overlay: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.6)', zIndex: 1000, display: 'flex', justifyContent: 'center', alignItems: 'flex-start', overflowY: 'auto', padding: '40px 20px' },
  modal: { backgroundColor: 'white', borderRadius: '20px', width: '100%', maxWidth: '480px', overflow: 'hidden', marginBottom: '40px' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', padding: '24px 24px 16px' },
  title: { fontSize: '18px', fontWeight: '800', color: '#000' },
  subtitle: { fontSize: '12px', color: '#888', margin: '4px 0 0' },
  closeButton: { background: 'none', border: 'none', fontSize: '24px', cursor: 'pointer', color: '#888', lineHeight: 1 },
  toggle: { display: 'flex', margin: '0 24px 16px', backgroundColor: '#F5F5EF', borderRadius: '10px', padding: '4px' },
  toggleBtn: { flex: 1, padding: '8px', border: 'none', borderRadius: '8px', fontSize: '13px', fontWeight: '600', cursor: 'pointer', backgroundColor: 'transparent', color: '#888' },
  toggleBtnActive: { backgroundColor: '#000', color: '#fff' },
  media: { width: '100%', aspectRatio: TARGET_ASPECT_RATIO, objectFit: 'cover', display: 'block' },
  mediaPlaceholder: { height: '200px', backgroundColor: '#F5F5EF', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#888', fontSize: '14px' },
  gallery: { display: 'flex', gap: '4px', padding: '4px', backgroundColor: '#F5F5EF', overflowX: 'auto' },
  thumb: { width: '64px', height: `${64 / TARGET_ASPECT_RATIO}px`, objectFit: 'cover', flexShrink: 0, cursor: 'pointer' },
  thumbContainer: { position: 'relative', width: '64px', height: '64px', flexShrink: 0, borderRadius: '6px', overflow: 'hidden', cursor: 'pointer' },
  reelOverlay: { position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: 'rgba(0,0,0,0.3)', color: 'white', fontSize: '18px' },
  body: { padding: '20px 24px 32px' },
  companyName: { fontSize: '26px', fontWeight: '800', color: '#000', margin: '0 0 4px 0' },
  differentiators: { fontSize: '11px', color: '#000', fontWeight: '600', textAlign: 'right', maxWidth: '140px', lineHeight: 1.6 },
  typeBadge: { display: 'inline-block', padding: '4px 12px', backgroundColor: '#F5F5EF', borderRadius: '6px', fontSize: '12px', fontWeight: '700', color: '#4A3550', margin: '8px 0' },
  location: { fontSize: '14px', color: '#555', margin: '4px 0 8px' },
  detailRow: { fontSize: '13px', color: '#555', margin: '4px 0', display: 'flex', alignItems: 'center', gap: '6px' },
  detailIcon: { fontSize: '16px', color: '#000', flexShrink: 0 },
  sectionTitle: { fontSize: '15px', fontWeight: '700', color: '#000', margin: '0 0 8px 0' },
  bio: { fontSize: '14px', color: '#555', lineHeight: 1.7, margin: 0 },
  centeredRow: { fontSize: '14px', color: '#555', margin: '6px 0', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' },
  socialIcon: { width: '28px', height: '28px' },
  socialLink: { display: 'flex', alignItems: 'center', justifyContent: 'center' },
  swipeCard: { backgroundColor: '#F5F5EF', overflow: 'hidden' },
  swipeMedia: { width: '100%', aspectRatio: TARGET_ASPECT_RATIO, objectFit: 'cover', display: 'block' },
  reelLabel: { position: 'relative', display: 'flex', alignItems: 'center', gap: '4px', backgroundColor: 'rgba(0,0,0,0.4)', borderRadius: '20px', padding: '4px 10px', width: 'fit-content', margin: '-28px 0 0 12px', zIndex: 1 },
  swipeInfo: { padding: '12px 16px 20px' },
  swipeName: { fontSize: '20px', fontWeight: '700', color: '#2D2D2D' },
  swipeDiff: { fontSize: '11px', color: '#000', fontWeight: '600', textAlign: 'right', maxWidth: '120px', lineHeight: 1.5 },
  swipeBadge: { display: 'inline-block', padding: '4px 10px', backgroundColor: 'rgba(74,53,80,0.15)', borderRadius: '8px', fontSize: '12px', fontWeight: '700', color: '#4A3550' },
};

const modalStyles = {
  overlay: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', justifyContent: 'center', alignItems: 'flex-start', overflowY: 'auto', padding: '40px 20px', zIndex: 1000 },
  modal: { backgroundColor: 'white', borderRadius: '20px', padding: '32px', width: '100%', maxWidth: '560px' },
  modalHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' },
  modalTitle: { fontSize: '20px', fontWeight: '800', color: '#000000' },
  closeButton: { background: 'none', border: 'none', fontSize: '24px', cursor: 'pointer', color: '#888', lineHeight: 1 },
  field: { marginBottom: '20px' },
  label: { display: 'block', fontSize: '13px', fontWeight: '600', color: '#444', marginBottom: '8px' },
  input: { width: '100%', padding: '12px 14px', borderRadius: '10px', border: '1.5px solid #e0e0e0', fontSize: '14px', outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit' },
  chipRow: { display: 'flex', gap: '8px', flexWrap: 'wrap' },
  chip: { padding: '8px 14px', borderRadius: '18px', border: '1.5px solid #e0e0e0', backgroundColor: 'white', color: '#666', fontSize: '13px', fontWeight: '600', cursor: 'pointer', outline: 'none' },
  chipSelected: { backgroundColor: '#000000', border: '1.5px solid #000000', color: 'white' },
  checkboxRow: { display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '20px' },
  checkboxLabel: { fontSize: '14px', color: '#444' },
  saveButton: { width: '100%', padding: '14px', backgroundColor: '#000000', color: 'white', border: 'none', borderRadius: '12px', fontSize: '15px', fontWeight: '700', cursor: 'pointer', marginTop: '8px' },
};

const styles = {
  container: { minHeight: '100vh', backgroundColor: '#F5F5EF', fontFamily: "'Inter', sans-serif" },
  loading: { display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', fontFamily: "'Inter', sans-serif" },
  nav: { display: 'flex', alignItems: 'center', gap: '16px', padding: '16px 32px', backgroundColor: 'white', borderBottom: '1px solid #e8e8e0', position: 'sticky', top: 0, zIndex: 100, flexWrap: 'wrap' },
  backButton: { backgroundColor: 'transparent', border: 'none', color: '#555', fontSize: '14px', fontWeight: '600', cursor: 'pointer', padding: '8px 0' },
  navTitle: { fontSize: '16px', fontWeight: '700', color: '#000000', flex: 1 },
  navActions: { display: 'flex', alignItems: 'center', gap: '10px' },
  approveButton: { padding: '8px 20px', backgroundColor: '#4CAF50', color: 'white', border: 'none', borderRadius: '8px', fontSize: '13px', fontWeight: '700', cursor: 'pointer' },
  rejectButton: { padding: '8px 20px', backgroundColor: '#E53935', color: 'white', border: 'none', borderRadius: '8px', fontSize: '13px', fontWeight: '700', cursor: 'pointer' },
  statusBadge: { fontSize: '12px', fontWeight: '700', padding: '6px 12px', borderRadius: '20px' },
  content: { maxWidth: '900px', margin: '0 auto', padding: '40px 24px' },
  section: { backgroundColor: 'white', borderRadius: '16px', padding: '24px', marginBottom: '20px', boxShadow: '0 2px 12px rgba(0,0,0,0.06)' },
  sectionTitle: { fontSize: '16px', fontWeight: '700', color: '#000000', marginBottom: '16px', paddingBottom: '12px', borderBottom: '1px solid #f0f0f0' },
  sectionContent: {},
  row: { display: 'flex', padding: '6px 0', borderBottom: '1px solid #f8f8f8' },
  rowLabel: { width: '160px', fontSize: '13px', color: '#888', fontWeight: '500', flexShrink: 0 },
  rowValue: { fontSize: '13px', color: '#2D2D2D', fontWeight: '600', flex: 1 },
  imageGrid: { display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '8px', marginTop: '12px' },
  image: { width: '100%', aspectRatio: TARGET_ASPECT_RATIO, objectFit: 'cover', borderRadius: '8px' },
  imageRemove: { position: 'absolute', top: '4px', right: '4px', width: '20px', height: '20px', borderRadius: '50%', backgroundColor: 'rgba(0,0,0,0.6)', color: 'white', border: 'none', cursor: 'pointer', fontSize: '14px', lineHeight: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' },
  imageEdit: { position: 'absolute', bottom: '4px', left: '4px', width: '22px', height: '22px', borderRadius: '50%', backgroundColor: 'rgba(0,0,0,0.6)', color: '#fff', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' },
  addMediaButton: { display: 'block', padding: '10px 16px', backgroundColor: '#000000', color: 'white', border: 'none', borderRadius: '8px', fontSize: '13px', fontWeight: '600', cursor: 'pointer', textAlign: 'center' },
  editButton: { background: 'none', border: 'none', color: '#000000', fontSize: '13px', fontWeight: '600', cursor: 'pointer' },
};